<template>
  <div class="objective-detail-panel">
    <loading-panel :loading="saving"></loading-panel>

    <!-- Details section -->
    <div class="drawer-section">
      <div class="section-header always-visible">
        <i class="tim-icons icon-bullet-list-67 mr-2"></i>
        <span>Details</span>
      </div>
      <div class="section-content">
        <div class="row">
          <div class="col-12">
            <base-input label="Name" v-model="localObjective.name"
                        placeholder="Objective name"></base-input>
          </div>
        </div>
        <div class="row" v-if="availableParents.length > 0">
          <div class="col-12">
            <base-input label="Parent objective">
              <el-select v-model="localObjective.parent_id"
                         class="select-primary"
                         placeholder="None (top level)"
                         clearable>
                <el-option :label="'None (top level)'" :value="null"></el-option>
                <el-option v-for="parent in availableParents"
                           :key="'parent-' + parent.id"
                           :label="parent.name || 'Untitled'"
                           :value="parent.id"
                           class="select-primary">
                </el-option>
              </el-select>
            </base-input>
          </div>
        </div>
        <div class="row">
          <div class="col-12">
            <base-input label="Objective description">
              <textarea class="form-control" v-model="localObjective.description"
                        placeholder="Objective description" rows="2"></textarea>
            </base-input>
          </div>
        </div>
        <div class="row">
          <div class="col-12">
            <base-input label="Objective goal">
              <textarea class="form-control" v-model="localObjective.solution"
                        placeholder="Objective goal" rows="2"></textarea>
            </base-input>
          </div>
        </div>
        <div class="row">
          <div class="col-12">
            <label style="font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; color: #9a9a9a;">
              Objective image
            </label>
            <div class="image-upload-area">
              <div v-if="localObjective.solution_img" class="image-preview">
                <img :src="localObjective.solution_img" />
                <button class="image-remove-btn" @click="localObjective.solution_img = ''">
                  <i class="tim-icons icon-simple-remove"></i>
                </button>
              </div>
              <label v-else class="image-placeholder">
                <i class="tim-icons icon-camera-18"></i>
                <span>Upload image</span>
                <input type="file" accept="image/*" @change="handleImageUpload"
                       style="display: none;" />
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Customer interactions section -->
    <div class="drawer-section">
      <div class="section-header always-visible">
        <div class="d-flex align-items-center flex-grow-1">
          <i class="tim-icons icon-chat-33 mr-2"></i>
          <span>Customer interactions</span>
          <badge v-if="localClues.length > 0" type="info" class="ml-2">{{ localClues.length }}</badge>
        </div>
        <base-button v-if="!showInlineForm" size="sm" type="primary" @click="openInlineForm">
          <i class="tim-icons el-icon-plus mr-1"></i> Add interaction
        </base-button>
      </div>
      <div class="section-content">
        <div class="d-flex align-items-center p-2 border border-primary rounded mb-3">
          <i class="fas fa-info-circle mr-2 info-text"></i>
          <small class="info-text">Pre-defined interactions that can be sent to customers via the <a href="/games/clues/configure/display" target="_blank" class="comm-screen-link">communication screen</a>.</small>
        </div>

      <!-- Interaction list -->
      <div :key="'msgs-' + renderKey">
        <div v-if="localClues.length > 0">
          <div v-for="clue in localClues" :key="'msg-' + (clue.id || clue._tempId)"
               class="message-row" :class="{ 'message-row--editing': clue.editing }">

            <!-- View mode -->
            <template v-if="!clue.editing">
              <div class="flex-grow-1" style="min-width: 0;">
                <div class="d-flex align-items-center mb-1">
                  <span class="message-type-badge" :class="'message-type-badge--' + clue.type">{{ clue.type }}</span>
                  <span v-if="clue.type === 'text' && clue.text" class="ml-2 message-text">{{ clue.text }}</span>
                  <span v-if="clue.type !== 'text' && clue.file_url" class="ml-2 message-text">{{ getFileName(clue.file_url) }}</span>
                </div>
                <img v-if="clue.type === 'image' && clue.file_url"
                     :src="clue.file_url" class="message-thumbnail mb-1" />
                <small v-if="clue.description" class="d-block" style="color: #9a9a9a;">
                  {{ clue.description }}
                </small>
              </div>
              <div class="d-flex align-items-center ml-3 flex-shrink-0">
                <i class="tim-icons icon-pencil message-action-icon"
                   @click="editClue(clue)"></i>
                <i class="tim-icons icon-trash-simple message-action-icon ml-2"
                   @click="deleteHint(clue)"></i>
              </div>
            </template>

            <!-- Edit mode — full width -->
            <template v-else>
              <div class="w-100">
                <div class="mb-2">
                  <span class="message-type-badge" :class="'message-type-badge--' + clue.type">{{ clue.type }}</span>
                </div>
                <base-input v-if="clue.type === 'text'" label="Interaction content" class="mb-2"
                            :error="clue.editing ? editFormError : ''">
                  <textarea class="form-control" v-model="clue.text" @input="editFormError = ''"
                            placeholder="Enter the interaction text that will be shown to the customer" rows="3"></textarea>
                </base-input>
                <base-input label="Description (optional)" class="mb-2">
                  <textarea class="form-control" v-model="clue.description"
                            placeholder="Additional context for this interaction" rows="2"></textarea>
                </base-input>
                <div class="d-flex align-items-center">
                  <base-checkbox v-model="clue.count_as_hint">Count as a hint</base-checkbox>
                  <base-button size="sm" type="default" class="ml-auto" @click="saveHint(clue)">
                    Done
                  </base-button>
                </div>
              </div>
            </template>

          </div>
        </div>
        <div v-else-if="!showInlineForm" class="text-center py-3" style="opacity: 0.5;">
          <p class="mb-0">No interactions configured.</p>
        </div>
      </div>

      <!-- Inline add form -->
      <div v-if="showInlineForm" class="inline-add-form">
        <div class="row mb-2">
          <div class="col-12">
            <base-input label="Interaction type">
              <el-select v-model="newHint.type" class="select-primary"
                         @change="clearFile()" size="small">
                <el-option v-for="t in interactionTypes" :key="t" :label="t" :value="t"
                           class="select-primary"></el-option>
              </el-select>
            </base-input>
          </div>
        </div>
        <div class="row mb-2">
          <div class="col-12">
            <div v-if="newHint.type === 'Text'">
              <base-input label="Interaction content" :error="addFormError">
                <textarea class="form-control" v-model="newHint.text" @input="addFormError = ''"
                          placeholder="Enter the interaction text that will be shown to the customer" rows="3"></textarea>
              </base-input>
            </div>
            <div v-if="newHint.type === 'Image'">
              <base-input label="Upload image" :error="addFormError">
                <div v-if="fileName" class="mb-2">
                  <img class="inline-file-preview" :src="newHint.img" />
                </div>
                <div>
                  <span class="btn btn-info btn-sm btn-simple btn-file">Choose file
                    <input :key="uploadKey + 'B'" accept="image/*" @change="handleFilePreview"
                           type="file" class="valid" :multiple="false" />
                  </span>
                  <span v-if="fileName" class="ml-2" style="font-size: 12px; color: #9a9a9a;">{{ fileName }}</span>
                </div>
              </base-input>
            </div>
            <div v-if="newHint.type === 'Video'">
              <base-input label="Upload video" :error="addFormError">
                <base-alert type="info" style="font-size: 11px; padding: 8px 12px;">
                  <i class="tim-icons el-icon-info mr-1"></i>
                  Browsers may block autoplay with sound. Whitelist console.offthecouch.io in settings.
                </base-alert>
                <span class="btn btn-info btn-sm btn-simple btn-file">Choose file
                  <input :key="uploadKey + 'C'" accept="video/*" @change="handleFilePreview"
                         type="file" class="valid" :multiple="false" />
                </span>
                <span v-if="fileName" class="ml-2" style="font-size: 12px; color: #9a9a9a;">{{ fileName }}</span>
              </base-input>
            </div>
            <div v-if="newHint.type === 'Audio'">
              <base-input label="Upload audio" :error="addFormError">
                <base-alert type="info" style="font-size: 11px; padding: 8px 12px;">
                  <i class="tim-icons el-icon-info mr-1"></i>
                  Browsers may block autoplay with sound. Whitelist console.offthecouch.io in settings.
                </base-alert>
                <span class="btn btn-info btn-sm btn-simple btn-file">Choose file
                  <input :key="uploadKey + 'A'" accept="audio/*" @change="handleFilePreview"
                         type="file" class="valid" :multiple="false" />
                </span>
                <span v-if="fileName" class="ml-2" style="font-size: 12px; color: #9a9a9a;">{{ fileName }}</span>
              </base-input>
            </div>
          </div>
        </div>
        <div class="row mb-2">
          <div class="col-12">
            <base-input label="Description (optional)">
              <textarea class="form-control" v-model="newHint.description"
                        placeholder="Additional context for this interaction" rows="2"></textarea>
            </base-input>
          </div>
        </div>
        <div class="d-flex align-items-center">
          <base-checkbox v-model="newHint.count_as_hint" class="mr-3">Count as a hint</base-checkbox>
          <div class="ml-auto">
            <base-button size="sm" type="default" @click="cancelInlineForm">Cancel</base-button>
            <base-button size="sm" type="primary" class="ml-2" @click="createHint()">Add</base-button>
          </div>
        </div>
      </div>
      </div>
    </div>

  </div>
</template>

<script>
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'
import { Select, Option } from 'element-ui'
import { BaseAlert } from 'src/components'
import Badge from '@/components/Badge'
import swal from 'sweetalert2'
const axios = require('axios')

export default {
  name: 'ObjectiveDetailPanel',
  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    BaseAlert,
    Badge
  },
  props: {
    objective: { type: Object, required: true },
    gameId: { type: [Number, String], required: true },
    companyId: { type: String, required: true },
    companyGroupId: { type: [Number, String], required: true },
    availableParents: { type: Array, default: () => [] },
    isNew: { type: Boolean, default: false }
  },
  data() {
    let clues = (this.objective.clues || []).map(c => ({ ...c, editing: false }))
    return {
      localObjective: { ...this.objective },
      localClues: clues,
      tempIdCounter: 0,
      saving: false,
      renderKey: 0,
      showInlineForm: false,
      interactionTypes: ['Text', 'Image', 'Audio', 'Video'],
      newHint: {
        type: 'Text',
        text: '',
        description: '',
        file_url: '',
        count_as_hint: 1,
        img: ''
      },
      file: '',
      fileName: '',
      uploadKey: 0,
      addFormError: '',
      editFormError: ''
    }
  },
  methods: {
    getAuthToken() {
      return new Promise(function(resolve) {
        Auth.currentSession()
          .then(data => { resolve(data.idToken.jwtToken) })
          .catch(err => console.log(err))
      })
    },

    puzzleBaseUrl() {
      return API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/games/' + this.gameId + '/puzzles'
    },

    // --- Objective CRUD ---
    async saveObjective() {
      this.saving = true
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }

      let data = {
        name: this.localObjective.name,
        description: this.localObjective.description,
        solution: this.localObjective.solution,
        solution_img: this.localObjective.solution_img,
        parent_id: this.localObjective.parent_id || null,
        position: this.localObjective.position
      }

      if (this.isNew) {
        try {
          let response = await axios.post(this.puzzleBaseUrl(), data, config)
          let newId = response.data.id || response.data
          let parsedId = typeof newId === 'object' ? newId.id : parseInt(newId)

          // Save any pending interactions
          if (this.localClues.length > 0) {
            let updateData = { ...data }
            updateData.clues = this.localClues.map(c => ({
              text: c.text || '',
              type: c.type || 'text',
              description: c.description || '',
              file_url: c.file_url || '',
              count_as_hint: c.count_as_hint != null ? c.count_as_hint : 1
            }))
            let url = this.puzzleBaseUrl() + '/' + parsedId
            await axios.put(url, updateData, config)
          }

          this.saving = false
          let created = { ...this.localObjective, id: parsedId, clues: this.localClues }
          this.$emit('created', created)
        } catch (err) {
          this.saving = false
          console.log(err)
          if (err.response && err.response.status === 401) {
            this.$router.push('/forbidden')
          } else {
            swal('Error!', 'Something went wrong', 'error')
          }
        }
      } else {
        // Update existing objective with clues
        data.clues = this.localClues.map(c => ({
          id: c.id || undefined,
          text: c.text || '',
          type: c.type || 'text',
          description: c.description || '',
          file_url: c.file_url || '',
          count_as_hint: c.count_as_hint != null ? c.count_as_hint : 1
        }))
        let url = this.puzzleBaseUrl() + '/' + this.localObjective.id
        try {
          await axios.put(url, data, config)
          this.saving = false
          this.$emit('saved', this.localObjective)
        } catch (err) {
          this.saving = false
          console.log(err)
          if (err.response && err.response.status === 401) {
            this.$router.push('/forbidden')
          } else {
            swal('Error!', 'Something went wrong', 'error')
          }
        }
      }
    },

    async handleImageUpload(event) {
      let file = event.target.files[0]
      if (!file) return
      this.saving = true
      try {
        let token = await this.getAuthToken()
        let config = { headers: { Authorization: token } }
        let url = API_LOCATION + 'upload'
        let payload = { filePath: file.name, contentType: file.type }
        let res = await axios.post(url, payload, config)
        let signedUrl = res.data
        await axios.put(signedUrl, file, { headers: { 'Content-Type': file.type } })
        let imgUrl = signedUrl.split('?')[0]
        this.localObjective.solution_img = imgUrl
        if (!this.isNew) {
          await this.saveObjective()
        } else {
          this.saving = false
        }
      } catch (err) {
        this.saving = false
        console.log(err)
        swal('Error!', 'Failed to upload image', 'error')
      }
    },

    // --- Interaction CRUD (local state, saved via saveObjective) ---
    openInlineForm() {
      this.localClues.forEach(c => this.$set(c, 'editing', false))
      this.addFormError = ''
      this.showInlineForm = true
    },

    cancelInlineForm() {
      this.showInlineForm = false
      this.addFormError = ''
      this.clearFile()
      this.newHint = { type: 'Text', text: '', description: '', file_url: '', count_as_hint: 1, img: '' }
    },

    async createHint() {
      let type = this.newHint.type.toLowerCase()
      if (type === 'text' && !this.newHint.text.trim()) {
        this.addFormError = 'Interaction content is required.'
        return
      }
      if (type !== 'text' && !this.file) {
        this.addFormError = 'Please upload a file.'
        return
      }
      this.addFormError = ''
      this.saving = true
      if (type !== 'text') {
        await this.saveHintFile()
      }
      this.localClues.push({
        _tempId: ++this.tempIdCounter,
        text: this.newHint.text,
        type: type,
        description: this.newHint.description,
        file_url: this.newHint.file_url,
        count_as_hint: this.newHint.count_as_hint,
        editing: false
      })
      this.saving = false
      this.cancelInlineForm()
      this.renderKey++
    },

    editClue(clue) {
      this.localClues.forEach(c => this.$set(c, 'editing', false))
      this.showInlineForm = false
      this.editFormError = ''
      this.$set(clue, 'editing', true)
    },

    saveHint(hint) {
      if (hint.type === 'text' && (!hint.text || !hint.text.trim())) {
        this.editFormError = 'Interaction content is required.'
        return
      }
      if (hint.type !== 'text' && !hint.file_url) {
        this.editFormError = 'Please upload a file.'
        return
      }
      this.editFormError = ''
      this.$set(hint, 'editing', false)
    },

    deleteHint(hint) {
      let idx = this.localClues.findIndex(h => h === hint)
      if (idx >= 0) this.localClues.splice(idx, 1)
      this.renderKey++
    },

    // --- File upload ---
    clearFile() {
      this.file = ''
      this.fileName = ''
      this.addFormError = ''
      this.newHint.img = 'https://console.offthecouch.io/img/placeholder.jpg'
      this.uploadKey++
      this.newHint.file_url = ''
    },

    handleFilePreview(event) {
      let file = event.target.files[0]
      if (!file) return
      this.addFormError = ''
      this.newHint.img = URL.createObjectURL(file)
      this.file = file
      this.fileName = file.name
    },

    async saveHintFile() {
      if (this.file !== '') {
        let token = await this.getAuthToken()
        let config = { headers: { Authorization: token } }
        let url = API_LOCATION + 'upload/clues'
        let payload = { filePath: this.file.name, contentType: this.file.type }
        let res = await axios.post(url, payload, config)
        let uploadUrl = res.data
        await axios.put(uploadUrl, this.file, { headers: { 'Content-Type': this.file.type } })
        this.newHint.file_url = uploadUrl.split('?')[0]
      }
    },

    getFileName(fileUrl) {
      if (!fileUrl) return ''
      let parts = fileUrl.split('/')
      return parts[parts.length - 1]
    }
  }
}
</script>

<style scoped>
.drawer-section {
  margin-bottom: 16px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  overflow: hidden;
}

.section-header {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  background: rgba(255, 255, 255, 0.02);
}

.section-header i {
  flex-shrink: 0;
}

.section-header > span {
  flex-shrink: 0;
}

.section-header.always-visible {
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.section-content {
  padding: 16px;
}

.message-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.message-row:last-child {
  border-bottom: none;
}

.message-row--editing {
  background: rgba(255, 255, 255, 0.03);
  border-radius: 6px;
  padding: 12px;
  margin: 4px -12px;
  border-bottom-color: transparent;
}

.message-type-badge {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 6px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.5);
  flex-shrink: 0;
}

.message-type-badge--text { color: #1d8cf8; background: rgba(29, 140, 248, 0.1); }
.message-type-badge--image { color: #e14eca; background: rgba(225, 78, 202, 0.1); }
.message-type-badge--audio { color: #ff8d72; background: rgba(255, 141, 114, 0.1); }
.message-type-badge--video { color: #00f2c3; background: rgba(0, 242, 195, 0.1); }

.info-text {
  color: rgba(255, 255, 255, 0.9);
}

.message-action-icon {
  font-size: 11px;
  cursor: pointer;
  padding: 4px;
  color: rgba(255, 255, 255, 0.9);
  opacity: 0.6;
  transition: opacity 0.15s;
}

.message-action-icon:hover {
  opacity: 1;
}

.message-text {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.9);
}

.message-thumbnail {
  width: 80px;
  height: 50px;
  border-radius: 4px;
  object-fit: cover;
}

.inline-add-form {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 12px;
  margin-top: 4px;
}

.inline-file-preview {
  width: 100%;
  max-height: 120px;
  object-fit: cover;
  border-radius: 4px;
}

.image-upload-area {
  border: 2px dashed rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  overflow: hidden;
  transition: border-color 0.2s;
}

.image-upload-area:hover {
  border-color: rgba(225, 78, 202, 0.4);
}

.image-preview {
  position: relative;
}

.image-preview img {
  width: 100%;
  height: 160px;
  object-fit: cover;
  display: block;
}

.image-remove-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  background: rgba(0, 0, 0, 0.6);
  border: none;
  border-radius: 50%;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 10px;
  transition: background 0.2s;
}

.image-remove-btn:hover {
  background: rgba(255, 70, 70, 0.8);
}

.image-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.3);
  transition: color 0.2s;
}

.image-placeholder:hover {
  color: #e14eca;
}

.image-placeholder i {
  font-size: 24px;
  margin-bottom: 8px;
}

.image-placeholder span {
  font-size: 12px;
}

.comm-screen-link {
  color: #e14eca;
  text-decoration: underline;
}

.comm-screen-link:hover {
  color: #ba54f5;
}
</style>

<style>
/* Light mode overrides */
.white-content .drawer-section {
  background: #ffffff;
  border-color: #e3e3e3;
}

.white-content .section-header {
  color: #344675;
  background: #f8f9fa;
}

.white-content .section-header.always-visible {
  border-bottom-color: #e3e3e3;
}

.white-content .message-row {
  border-bottom-color: rgba(0, 0, 0, 0.06);
}

.white-content .message-row--editing {
  background: #f8f9fa;
}

.white-content .message-type-badge {
  background: rgba(0, 0, 0, 0.05);
  color: #9A9A9A;
}

.white-content .message-text {
  color: #344675;
}

.white-content .message-action-icon {
  color: #344675;
}

.white-content .info-text {
  color: #344675;
}

.white-content .has-danger label.error {
  color: #ec250d;
}

.white-content .inline-add-form {
  border-top-color: rgba(0, 0, 0, 0.1);
}

.white-content .image-upload-area {
  border-color: rgba(0, 0, 0, 0.15);
}

.white-content .image-placeholder {
  color: rgba(0, 0, 0, 0.3);
}

.white-content .image-placeholder:hover {
  color: #e14eca;
}
</style>
